import { LOYALTY_CONTENT, LOYALTY_CONTEXT } from '@/app/common/starTrack/events/utils/eventsUtils.ts';
import { getNightsFromToday } from '@/app/components/hotel-detail/common/dates';
import type { Localization } from '@/app/components/search/common/Localization.ts';

export const ROOMS_DETAIL_INFO = 'ROOMS_DETAIL_INFO';
export const ROOMS_DETAIL_INFO_VERSION = '3.0';

export type RoomsDetailInfoPayload = {
  searchId: string;
  productDetailKey: string;
  internalHotelId: number;
  providerId: string;
  roomId: string;
  name: string;
  description: string;
  amenitiesFlag: boolean;
  bedType: string;
  cancellationPolicy: string;
  mealPlanClass: string;
  myPrice: boolean;
  tshFlag: boolean;
  flashSalesFlag: boolean;
  roomPosition: number;
  imageGalleryQty: number;
  totalPrice: number;
  totalPriceEuro: number;
  currency: string;
  isAppFlag: boolean;
  isGeniusFlag: boolean;
  optionIndex: number;
  addedValueOffer: string;
  hotelFreeCancellationDays: number;
  desiredProfitPricingId: string;
  isLoyaltyFlag?: boolean;
  strikethroughTotalPriceEuro: number;
  domain: string;
  brand: string;
  market: string;
  language: string;
  content: string;
  page: LOYALTY_CONTEXT;
  discountLabel: string | null;
  discount: number | null; // %
  loggedIn: boolean;
  userLoyaltyEnrol: boolean;
  loyaltyTier: number | null;
};

export type RoomsDetailInfoParams = {
  mealPlan: MealPlanType;
  room: RoomType;
  // 0-based index of `room` within its collection; reported as a 1-based position.
  roomIndex: number;
  // Running counter across the whole room x mealPlan collection - computed by the caller,
  // since it depends on iteration order across the whole flatMap.
  optionIndex: number;
  searchId: string;
  productDetailKey: string;
  internalHotelId: number;
  isTopSecret: boolean;
  isFlashSale: boolean;
  hasLoyaltyRate: boolean;
  currency: string;
  app: boolean;
  localization: Pick<Localization, 'hostname' | 'brand' | 'lang' | 'country'>;
  userLoyaltyEnrol: boolean;
  loyaltyTier: number | null;
  loggedIn: boolean;
  discountLabel: string | null;
};

export const createRoomsDetailInfoPayload = ({
  mealPlan,
  room,
  roomIndex,
  optionIndex,
  searchId,
  productDetailKey,
  internalHotelId,
  isTopSecret,
  isFlashSale,
  hasLoyaltyRate,
  currency,
  app,
  localization,
  userLoyaltyEnrol,
  loyaltyTier,
  loggedIn,
  discountLabel,
}: RoomsDetailInfoParams): RoomsDetailInfoPayload => {
  const { hostname: domain, brand, lang: language, country: market } = localization;

  return {
    searchId,
    productDetailKey,
    internalHotelId,
    providerId: `${mealPlan.supplier.id}`,
    roomId: room.roomId,
    name: room.name,
    description: room.descriptions.map((description) => description.text).join(','),
    amenitiesFlag: room.amenities.length > 0,
    bedType: mealPlan.bedId,
    cancellationPolicy: mealPlan.cancellationPolicy.type,
    mealPlanClass: mealPlan.boardClass,
    myPrice: mealPlan.isMyPrice,
    tshFlag: isTopSecret,
    flashSalesFlag: isFlashSale,
    roomPosition: roomIndex + 1,
    imageGalleryQty: room.images.length,
    totalPrice: mealPlan.finalTotalPrice ? Math.trunc(mealPlan.finalTotalPrice) : 0,
    totalPriceEuro: mealPlan.finalTotalPriceEUR ? Math.trunc(mealPlan.finalTotalPriceEUR) : 0,
    currency,
    isAppFlag: app,
    isGeniusFlag: false, // TODO Review
    optionIndex,
    addedValueOffer: mealPlan.promotions?.map((promotion) => promotion.label).join(',') || '',
    hotelFreeCancellationDays:
      mealPlan.cancellationPolicy.type === 'REFUNDABLE' && mealPlan.cancellationPolicy.upTo
        ? getNightsFromToday(mealPlan.cancellationPolicy.upTo)
        : 0,
    desiredProfitPricingId: mealPlan.dynamicPricingId,
    isLoyaltyFlag: hasLoyaltyRate,
    strikethroughTotalPriceEuro: mealPlan.strikeThroughPriceEUR ? Math.trunc(mealPlan.strikeThroughPriceEUR) : 0,
    domain,
    brand,
    market,
    language,
    content: userLoyaltyEnrol ? LOYALTY_CONTENT.DISCOUNT : LOYALTY_CONTENT.SIGNUP,
    page: LOYALTY_CONTEXT.DETAIL,
    discountLabel,
    discount: mealPlan.discountPercentage ?? null,
    loggedIn,
    userLoyaltyEnrol,
    loyaltyTier,
  };
};
